import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { toast } from 'react-toastify';

import { useTranslation } from '../../../hooks/useTranslation';
import RegisterTab, { isCnpj, DOCUMENT_TYPES } from './RegisterTab';
import { validateCNPJ, validateCPF } from '../../../utils';
import { HOST_API } from '../../../config/hosts';
import RedscoreHistoryTab from './RedscorehistoryTab';
import Header from '../../../components/Header';
import { tokens } from '../../../theme';
import CustomerCard from '../CustomerCard';
import SimulationTab from './SimulationTab';
import api from '../../../services/api';

export const isValidCpfCnpj = (value) => {
  if (!value) return false;
  const digits = value.replace(/\D/g, '');
  if (digits.length === 11) return validateCPF(digits);
  if (digits.length === 14) return validateCNPJ(digits);
  return false;
};

const hasPartnerData = (obj) => Object.values(obj).some((v) => v?.toString().trim());

const buildSchema = (t) => {
  const partnerSchema = yup.object().shape({
    fullName: yup.string().test('required-if-filled', t('messages.fullNameRequired'), function (v) {
      return !(hasPartnerData(this.parent) && !v);
    }),
    email: yup
      .string()
      .email(t('messages.invalidEmail'))
      .test('required-if-filled', t('messages.emailRequired'), function (v) {
        return !(hasPartnerData(this.parent) && !v);
      }),
    phone: yup.string().nullable(),
    taxvar: yup.string().nullable(),
    document: yup.string().nullable(),
  });

  return yup.object().shape({
    fullName: yup.string().required(t('messages.fullNameRequired')),
    email: yup.string().email(t('messages.invalidEmail')).required(t('messages.emailRequired')),
    phone: yup.string().required(t('messages.phoneRequired')),
    taxvar: yup
      .string()
      .required(t('forms.cpfCnpj'))
      .test('cpf-cnpj-valid', t('forms.invalidCpfCnpj'), isValidCpfCnpj),
    cep: yup.string().required(t('messages.cepRequired')),
    state: yup.string().required(t('messages.stateRequired')),
    city: yup.string().required(t('messages.cityRequired')),
    address: yup.string().required(t('messages.addressRequired')),
    number: yup.string().required(t('messages.numberRequired')),
    neighborhood: yup.string().required(t('messages.neighborhoodRequired')),
    partners: yup.array().of(partnerSchema),
  });
};

const buildInitialValues = (data) => ({
  fullName: data?.full_name || '',
  email: data?.email || '',
  phone: data?.cellphone || '',
  taxvar: data?.taxvar || '',
  businessName: data?.custom_fields?.business_name || '',
  municipalRegistration: data?.custom_fields?.municipal_registration || '',
  stateRegistration: data?.custom_fields?.state_registration || '',
  taxRegime: data?.custom_fields?.tax_regime || '',
  financialEmail: data?.custom_fields?.financial_email || '',
  financialPhone: data?.custom_fields?.financial_phone || '',
  address: data?.address?.place || '',
  number: data?.address?.number || '',
  complement: data?.address?.complement || '',
  neighborhood: data?.address?.neighborhood || '',
  cep: data?.address?.zipcode || '',
  state: typeof data?.address?.region === 'string'
    ? data.address.region
    : data?.address?.region?.name || '',
  city: data?.address?.city || '',
  partners: data?.relationships?.length
    ? data.relationships.map((rel) => ({
        id: rel.people?.id || '',
        fullName: rel.people?.full_name || '',
        email: rel.people?.email || '',
        phone: rel.people?.phone || '',
        taxvar: rel.people?.taxvar || '',
        document: rel.people?.document || '',
      }))
    : [{ fullName: '', email: '', phone: '', taxvar: '', document: '' }],
});

const TABS = [
  { label: 'Cadastro', requiresId: false },
  { label: 'Histórico Redscore', requiresId: true },
  { label: 'Simulações', requiresId: true },
];

const CustomerForm = ({ isEditing = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [activeTab, setActiveTab] = useState(0);
  const [expandedCredit, setExpandedCredit] = useState(false);
  const [loadingCep, setLoadingCep] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialData, setInitialData] = useState(null);
  const [loadingData, setLoadingData] = useState(isEditing);

  const [documentsByType, setDocumentsByType] = useState(
    Object.fromEntries(DOCUMENT_TYPES.map(({ key }) => [key, null]))
  );

  const handleSetDocument = (typeKey, newFiles) => {
    setDocumentsByType((prev) => ({ ...prev, [typeKey]: newFiles[0] || null }));
  };

  const handleClearDocument = (typeKey) => {
    setDocumentsByType((prev) => ({ ...prev, [typeKey]: null }));
  };

  const fetchCustomer = useCallback(async () => {
    try {
      setLoadingData(true);
      const { data } = await api.get(`/v1/customer/${id}`);
      setInitialData(data);

      const savedDocuments = data?.custom_fields?.documents || [];
      const byFieldName = Object.fromEntries(
        savedDocuments.filter((d) => d.type).map((d) => [d.type, d])
      );
      setDocumentsByType(
        Object.fromEntries(
          DOCUMENT_TYPES.map(({ key, fieldName }) => [key, byFieldName[fieldName] || null])
        )
      );
    } catch (error) {
      toast.error(t('messages.errorFetchingData'));
      navigate('/customers');
    } finally {
      setLoadingData(false);
    }
  }, []);

  useEffect(() => {
    if (!isEditing || !id) return;
    fetchCustomer();
  }, [id, isEditing, fetchCustomer]);

  const fetchAddressByCep = async (cep, setFieldValue) => {
    const cleaned = cep.replace(/\D/g, '');
    if (cleaned.length !== 8) return;
    setLoadingCep(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cleaned}/json/`);
      const data = await res.json();
      if (data.erro) {
        toast.error(t('messages.zipCodeNotFound'));
        return;
      }
      setFieldValue('address', data.logradouro || '');
      setFieldValue('state', data.uf || '');
      setFieldValue('city', data.localidade || '');
      setFieldValue('neighborhood', data.bairro || '');
    } catch {
      toast.error(t('messages.errorFetchingData'));
    } finally {
      setLoadingCep(false);
    }
  };

  const handleFormSubmit = async (values) => {
    const cleanPartners = isCnpj(values.taxvar) ? values.partners.filter(hasPartnerData) : [];

    const formData = new FormData();

    Object.entries(values).forEach(([key, value]) => {
      if (key === 'partners') return;
      if (value !== '' && value !== null && value !== undefined) {
        formData.append(key, value);
      }
    });

    formData.append('partners', JSON.stringify(cleanPartners));

    const existingDocs = [];
    DOCUMENT_TYPES.forEach(({ key, fieldName }) => {
      const doc = documentsByType[key];
      if (!doc) return;
      if (doc instanceof File) {
        formData.append(`documents[${fieldName}]`, doc);
      } else {
        existingDocs.push({ ...doc, type: fieldName });
      }
    });
    if (existingDocs.length) {
      formData.append('existingDocuments', JSON.stringify(existingDocs));
    }

    try {
      setLoading(true);

      if (isEditing) {
        await api.put(`/v1/customer/${id}`, formData);
        toast.success(t('messages.clientUpdatedSuccess'));
      } else {
        await api.post(`${HOST_API}/v1/customer`, formData);
        toast.success(t('messages.clientRegisteredSuccess'));
      }

      navigate('/app/customers');
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.errorRegisterClient'));
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Button
        onClick={() => navigate('/app/customers')}
        sx={{
          display: 'flex',
          alignItems: 'center',
          color: colors.grey[300],
          textTransform: 'none',
          padding: 0,
          gap: 1,
          marginBottom: 3,
          '&:hover': { backgroundColor: 'transparent' },
        }}
      >
        <ArrowBackIcon />
        <Typography fontSize={14} fontWeight={400}>
          {t('customers.backToCustomer')}
        </Typography>
      </Button>

      <Header
        title={isEditing ? t('titles.customerDetails') : t('titles.registerCustomer')}
        subtitle={isEditing ? t('titles.editCustomerSubtitle') : t('titles.registerNewCustomer')}
      />

      {isEditing && <CustomerCard data={initialData} />}

      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            px: 3,
            borderBottom: '1px solid #e5e7eb',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: 14 },
            '& .Mui-selected': { color: '#000', fontWeight: 600 },
            '& .MuiTabs-indicator': { backgroundColor: colors.greenAccent[500], height: 3 },
          }}
        >
          {TABS.map((tab) => (
            <Tab key={tab.label} label={tab.label} disabled={tab.requiresId && !isEditing} />
          ))}
        </Tabs>

        <Box p={3}>
          {activeTab === 0 && (
            <Formik
              enableReinitialize
              initialValues={buildInitialValues(initialData)}
              validationSchema={buildSchema(t)}
              onSubmit={handleFormSubmit}
            >
              {(formikProps) => (
                <RegisterTab
                  {...formikProps}
                  loading={loading}
                  expandedCredit={expandedCredit}
                  setExpandedCredit={setExpandedCredit}
                  loadingCep={loadingCep}
                  fetchAddressByCep={fetchAddressByCep}
                  documentsByType={documentsByType}
                  onSetDocument={handleSetDocument}
                  onClearDocument={handleClearDocument}
                />
              )}
            </Formik>
          )}

          {activeTab === 1 && <RedscoreHistoryTab orders={initialData?.orders ?? []} />}
          {activeTab === 2 && <SimulationTab customerId={id} />}
        </Box>
      </Paper>
    </Box>
  );
};

export default CustomerForm;
